/**
 * Client-side analytics helper.
 *
 * Usage:
 *   import { track, trackError, submitFeedback } from "@/lib/analytics";
 *
 *   track("exam_started", { exam_type: "sat", session_id: "..." });
 *   trackError(error, { where: "/api/score" });
 *   await submitFeedback({ kind: "global", message: "..." });
 *
 * Design notes:
 * - `track()` is fire-and-forget. It never throws, never blocks the caller.
 * - Events are queued in memory and flushed in small batches every 2s, or
 *   immediately on page hide. Keeps network traffic low during bursts.
 * - If the user is logged out, events are silently dropped server-side.
 * - `submitFeedback()` is NOT queued — it's awaitable so UIs can show toasts.
 */

import { captureToPostHog } from "@/components/posthog-provider";

type EventData = Record<string, unknown>;

interface QueuedEvent {
  event_type: string;
  event_data: EventData;
  url: string;
}

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const FLUSH_INTERVAL_MS = 2000;
const MAX_BATCH = 20;

/**
 * Sampling rates per event type. 1.0 = keep all, 0.2 = keep 20%, etc.
 * Only the high-volume / low-value events should be sampled — errors,
 * conversion-funnel markers, and user feedback always stay at 100%.
 *
 * Background: page_view fires on every client-side navigation. Across all
 * active users this dominates user_events writes. Dropping 80% of
 * page_views still gives statistically useful traffic data while cutting
 * the steady write load by ~5×. See docs/supabase-io-audit-2026-05-27.md
 * item #2.
 */
const SAMPLE_RATES: Record<string, number> = {
  page_view: 0.2, // keep 20%
};

function shouldSample(eventType: string): boolean {
  const rate = SAMPLE_RATES[eventType];
  if (rate === undefined || rate >= 1) return true; // always keep
  if (rate <= 0) return false;                       // always drop
  return Math.random() < rate;
}

function scheduleFlush() {
  if (flushTimer != null) return;
  flushTimer = setTimeout(flushNow, FLUSH_INTERVAL_MS);
}

function flushNow() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;

  const batch = queue.splice(0, MAX_BATCH);
  const body = JSON.stringify({ events: batch });

  // Prefer sendBeacon for page-hide flushes, fall back to fetch.
  if (typeof navigator !== "undefined" && "sendBeacon" in navigator) {
    try {
      const ok = navigator.sendBeacon(
        "/api/events",
        new Blob([body], { type: "application/json" })
      );
      if (ok) {
        // If there are still events queued, schedule another flush
        if (queue.length > 0) scheduleFlush();
        return;
      }
    } catch {
      /* fall through to fetch */
    }
  }

  fetch("/api/events", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  })
    .catch(() => {
      /* silently swallow — analytics must never break the app */
    })
    .finally(() => {
      if (queue.length > 0) scheduleFlush();
    });
}

/**
 * Fire-and-forget event tracking.
 * Safe to call from anywhere, including server components (no-op on server).
 */
export function track(event_type: string, event_data: EventData = {}) {
  if (typeof window === "undefined") return; // SSR safety
  try {
    // Sampling — drop a fraction of high-volume / low-value events to
    // reduce Supabase write load. PostHog still gets every event because
    // their pricing is per-event and they handle the volume natively.
    if (!shouldSample(event_type)) {
      captureToPostHog(event_type, event_data);
      return;
    }
    queue.push({
      event_type,
      event_data,
      url: window.location.pathname + window.location.search,
    });
    // If the queue is getting big, flush sooner
    if (queue.length >= MAX_BATCH) {
      flushNow();
    } else {
      scheduleFlush();
    }
    // Dual-write to PostHog (no-op if PostHog isn't initialized)
    captureToPostHog(event_type, event_data);
  } catch {
    /* never throw from analytics */
  }
}

/** Error events recorded since the page loaded; reset on client-side navigation. */
let errorsThisPage = 0;
const MAX_ERRORS_PER_PAGE = 25;
let errorsPagePath = "";

/**
 * Track a JS error. Extracts message + stack for the server.
 * Accepts either an Error instance or a string.
 */
export function trackError(err: unknown, extra: EventData = {}) {
  if (typeof window !== "undefined" && window.location.pathname !== errorsPagePath) {
    errorsPagePath = window.location.pathname;
    errorsThisPage = 0;
  }
  try {
    let message: string;
    let stack: string | undefined;
    if (err instanceof Error) {
      message = err.message;
      stack = err.stack;
    } else if (typeof err === "string") {
      message = err;
    } else {
      message = "unknown_error";
    }
    // Suppress known benign noise that isn't actionable:
    // - Supabase auth-token lock contention (multiple tabs / concurrent
    //   refresh of the same token). Both old + new wordings:
    if (message.includes("lock:sb-") && message.includes("was released because another request stole it")) return;
    if (message.includes("Lock broken by another request with the 'steal' option")) return;
    // - Webpack chunk-load failures during a Vercel deploy (stale bundle
    //   on the user's machine, new chunk IDs on the server). Self-resolves
    //   on hard refresh; not a code bug.
    if (message.includes("is not a function") && /e\[o\]\(.*a\.exports/.test(message)) return;
    if (message.includes("ChunkLoadError") || message.includes("Loading chunk") || message.includes("Loading CSS chunk")) return;
    // - Crypto-wallet browser extensions (MetaMask etc.) erroring on pages
    //   that never call them. Not our code.
    if (message.includes("MetaMask")) return;
    // - Opaque cross-origin errors. Browsers collapse any error thrown by a
    //   script served from another origin (browser extensions, injected
    //   third-party code) to the exact string "Script error." with lineno 0
    //   and an empty filename — every actionable detail is stripped by the
    //   sandbox, so it is undebuggable by construction. Our own first-party
    //   errors are never masked this way (same-origin chunks + production
    //   sourcemaps, see next.config.ts), so anything that arrives as a bare
    //   "Script error." is provably not our code. Guard on an exact match so
    //   a longer message that merely contains the phrase still logs.
    if (message.trim() === "Script error." || message.trim() === "Script error") return;
    // - Errors thrown by browser extensions injected into the page. The stack (or the
    //   window.onerror filename) names the extension origin; our bundles never do.
    //   2026-09-21: one user's extension threw 1,044 times in 25 minutes on two pages.
    const origin = `${stack ?? ""} ${String(extra.filename ?? "")}`;
    if (/(chrome|moz|safari|safari-web|ms-browser)-extension:\/\//.test(origin)) return;
    // - Flood guard: a page stuck in an error loop must not write hundreds of rows.
    if (errorsThisPage >= MAX_ERRORS_PER_PAGE) return;
    errorsThisPage++;
    track("error", {
      message: message.slice(0, 500),
      stack: stack?.slice(0, 2000),
      ...extra,
    });
  } catch {
    /* never throw from analytics */
  }
}

/**
 * Instrument a user-triggered action so success / failure is always logged.
 *
 * Wrap any click handler that calls an API. The helper records three events:
 *   • `{name}_started`   — always, when the action kicks off
 *   • `{name}_succeeded` — when `fn` resolves without throwing
 *   • `{name}_failed`    — when `fn` throws, OR returns `{ ok: false }`
 *                          (also fires a standard `error` event via trackError)
 *
 * Why this exists: users report things like "I clicked Grade My Exam and
 * nothing happened." Without this, failures in fetch / parsing / redirect
 * are invisible. With it, the error shows up in the admin Errors tab with
 * enough context (action name + extra data) to debug.
 *
 * Usage:
 *   await trackAction("score_exam", { session_id, exam_type }, async () => {
 *     const res = await fetch(`/api/sessions/${id}/score`, { method: "POST" });
 *     if (!res.ok) throw new Error(`score_failed_${res.status}`);
 *     router.push(`/report/${id}`);
 *   });
 */
export async function trackAction<T>(
  name: string,
  extra: EventData,
  fn: () => Promise<T>
): Promise<T> {
  const started = Date.now();
  track(`${name}_started`, extra);
  try {
    const result = await fn();
    track(`${name}_succeeded`, { ...extra, duration_ms: Date.now() - started });
    return result;
  } catch (err) {
    track(`${name}_failed`, {
      ...extra,
      duration_ms: Date.now() - started,
      message: err instanceof Error ? err.message : String(err),
    });
    trackError(err, { action: name, ...extra });
    throw err;
  }
}

/**
 * Register global listeners for uncaught errors + promise rejections.
 * Should be called exactly once, from a client component in the layout.
 */
export function installGlobalErrorHandlers() {
  if (typeof window === "undefined") return;
  if ((window as unknown as { __fpt_err_installed?: boolean }).__fpt_err_installed) return;
  (window as unknown as { __fpt_err_installed?: boolean }).__fpt_err_installed = true;

  window.addEventListener("error", (e) => {
    trackError(e.error ?? e.message ?? "window.onerror", {
      filename: e.filename,
      lineno: e.lineno,
      source: "window.onerror",
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    trackError(e.reason ?? "unhandled_rejection", {
      source: "unhandledrejection",
    });
  });

  // Flush any pending events on page hide
  window.addEventListener("pagehide", flushNow);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushNow();
  });
}

/* ------------------------------------------------------------------ */
/*  Feedback submission (awaitable, separate from the event queue)     */
/* ------------------------------------------------------------------ */

export type FeedbackKind = "global" | "question" | "exam" | "report" | "error";

export interface FeedbackInput {
  kind: FeedbackKind;
  category?: string;
  rating?: number;
  message?: string;
  context?: Record<string, unknown>;
}

export interface FeedbackResult {
  ok: boolean;
  id?: number;
  error?: string;
}

/**
 * Submit a piece of user feedback. Awaitable so the UI can show a toast
 * on success / error.
 */
export async function submitFeedback(input: FeedbackInput): Promise<FeedbackResult> {
  try {
    const res = await fetch("/api/user-feedback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const data = (await res.json().catch(() => ({}))) as FeedbackResult;
    if (!res.ok) {
      return { ok: false, error: data.error ?? "submit_failed" };
    }
    // Also fire a lightweight analytics event so we can count feedback volume
    track("feedback_submitted", {
      kind: input.kind,
      category: input.category,
      has_rating: input.rating != null,
      has_message: !!(input.message && input.message.trim()),
    });
    return { ok: true, id: data.id };
  } catch {
    return { ok: false, error: "network_error" };
  }
}
